/**
 * Meter-exhausted messaging, as data: the exhausted paywall dialog's context
 * line and benefit bullets, the "0 left" strip above article lists, and the
 * confirm-last-unlock body. Both apps render these strings verbatim so the
 * copy (and its period words) cannot drift — every period word comes from the
 * injected periodCopy (meterPeriodCopy), never inlined here.
 *
 * Never fake a count: a null count renders copy without a number, not a
 * placeholder.
 */

/**
 * The exhausted dialog's meter context line, shown under the subtitle.
 * With a title (a specific locked article was tapped), the article is named;
 * without one (opened from the meter strip) the line stays generic.
 *
 * @param {Object} inputs
 * @param {number} inputs.limit
 * @param {{ thisPeriod: string, resetsAdverb: string }} inputs.periodCopy
 * @param {string|null} [inputs.articleTitle]
 * @returns {string}
 */
export function buildExhaustedContextLine({ limit, periodCopy, articleTitle = null }) {
  if (articleTitle) {
    return `You've used all ${limit} unlocks ${periodCopy.thisPeriod} — go unlimited to read “${articleTitle}” now, or they reset ${periodCopy.resetsAdverb}.`;
  }
  return `You've used all ${limit} unlocks ${periodCopy.thisPeriod} — they reset ${periodCopy.resetsAdverb}, or go unlimited.`;
}

/** English list join: A / A and B / A, B, and C. */
function joinNames(names) {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * The exhausted dialog's benefit bullets, article-first. Order is the spec:
 * current-exam articles (with the "no daily/weekly limit" framing), practice
 * questions, readiness, multi-exam bundle list LAST (only when there are 2+
 * exams to list).
 *
 * `articleCount` must be the EXAM-WIDE total (from the app's content context)
 * — never a topic's count — no matter which surface opened the dialog.
 *
 * @param {Object} inputs
 * @param {number|null} [inputs.articleCount]      exam-wide article total; null → no number
 * @param {string} [inputs.examTitle]              current exam display name
 * @param {number|string|null} [inputs.questionCount]  null → no number (never a fake fallback)
 * @param {string[]} [inputs.bundleExamTitles]     all exam titles in the bundle
 * @param {{ resetsAdverb: string }} inputs.periodCopy
 * @returns {string[]}
 */
export function buildExhaustedDialogBullets({
  articleCount = null,
  examTitle = '',
  questionCount = null,
  bundleExamTitles = [],
  periodCopy,
}) {
  const examPart = examTitle ? `${examTitle} ` : '';
  const bullets = [
    articleCount != null
      ? `All ${articleCount} ${examPart}study articles — no ${periodCopy.resetsAdverb} limit`
      : `All ${examPart}study articles — no ${periodCopy.resetsAdverb} limit`,
    questionCount != null
      ? `${questionCount} practice questions with detailed explanations`
      : 'Practice questions with detailed explanations',
    'Track exam readiness with Readiness Score',
  ];
  if (bundleExamTitles.length >= 2) {
    bullets.push(`Full access across ${joinNames(bundleExamTitles)}`);
  }
  return bullets;
}

/**
 * The slim exhausted strip above article lists (never the related rail).
 * null while unlocks remain — the strip only exists at zero.
 *
 * @param {Object} inputs
 * @param {number} inputs.remaining
 * @param {number} inputs.limit
 * @param {{ thisPeriod: string, resetsAdverb: string }} inputs.periodCopy
 * @returns {{ label: string, ctaLabel: string }|null}
 */
export function buildMeterStrip({ remaining, limit, periodCopy }) {
  if (remaining > 0) return null;
  return {
    label: `0 of ${limit} left ${periodCopy.thisPeriod} · resets ${periodCopy.resetsAdverb}`,
    ctaLabel: 'Go unlimited',
  };
}

/**
 * The confirm-last-unlock body — no meter numbers, just the period.
 *
 * @param {{ thisPeriod: string, resetsAdverb: string }} periodCopy
 * @returns {string}
 */
export function buildLastUnlockConfirmBody(periodCopy) {
  return `This uses your last unlock for ${periodCopy.thisPeriod}. Unlocked articles stay open forever. Unlocks reset ${periodCopy.resetsAdverb}.`;
}

/**
 * The anonymous free-reads strip (related rail header, pre-signup surfaces).
 * Unlike buildMeterStrip this ALWAYS returns — the pool is invisible unless
 * announced, so the strip exists at every count. `spent` lets the diagnostic
 * restyle the zero state (message only — it must never navigate mid-flow).
 *
 * @param {Object} inputs
 * @param {number} inputs.remaining   anonymous reads left on this device
 * @param {number} inputs.limit      device pool size
 * @returns {{ label: string, spent: boolean }}
 */
export function buildAnonymousReadsStrip({ remaining, limit }) {
  if (remaining <= 0) {
    return { label: "You've used your free reads — sign up free to keep reading", spent: true };
  }
  if (remaining >= limit) {
    return { label: `${limit} free reads — no account needed`, spent: false };
  }
  return { label: `${remaining} of ${limit} free reads left`, spent: false };
}
